// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { SessionSalespersonPropertyModel } from "@store/Framework/Types/ApiModels";
import tryJsonParse from "@store/Framework/Common/Utilities/tryJsonParse";
import Icon, { IconPresentationProps } from "@store/MobiusOverrides/Icon/Icon";
import { boxStyles } from "@store/Themes/common/mixins/box";
import EmailLine from "@store/Library/Components/Toolkit/Icons/Email/EmailLine";
import PhoneLine from "@store/Library/Components/Toolkit/Icons/Phone/PhoneLine";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useSession } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { FC } from "react";
import { css } from "styled-components";

export interface AccountSettingsSalesRepInfosStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    innerWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    nameText?: TypographyProps;
    infoText?: TypographyProps;
    emailIcon?: IconPresentationProps;
    phoneIcon?: IconPresentationProps;
}

const accountSettingsSalesRepInfosStyles: AccountSettingsSalesRepInfosStyles = {
    wrapper: {
        css: css`
            ${boxStyles(pxToRemArray([16]), true)}
            display: flex;
            flex-direction: column;
        `,
    },
    title: {
        as: "span",
        css: css`
            display: inline-block;
            font-size: ${x => x.theme.design.typography.size.small};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            margin-bottom: ${pxToRem(6)};
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
        `,
    },
    infoWrapper: {
        css: css`
            /* spacing is the full gap; the rule is centred, so each side gets 16px */
            ${inlineSeparatorStyles({ spacing: pxToRem(32), color: "common.border" })}
        `,
    },
    nameText: {
        as: "span",
        css: css`
            display: inline-flex;
            align-items: center;
        `,
    },
    infoText: {
        as: "span",
        css: css`
            display: inline-flex;
            align-items: center;
        `,
    },
    emailIcon: {
        src: EmailLine,
        size: 18,
        css: css`
            margin-right: ${pxToRem(4)};
        `,
    },
    phoneIcon: {
        src: PhoneLine,
        size: 18,
        css: css`
            margin-right: ${pxToRem(4)};
        `,
    },
};

const styles = accountSettingsSalesRepInfosStyles;

const AccountSettingsSalesRepInfos: FC<WidgetProps> = () => {
    const session = useSession();
    const salespersonData = tryJsonParse<SessionSalespersonPropertyModel>(session.properties.salesperson);

    if (!salespersonData) {
        return null;
    }

    const { salesperson: salesRep, telOffice } = salespersonData;

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="salesPersonInfo">
            <Typography {...styles.title}>{translate("Account Manager")}</Typography>
            <StyledWrapper {...styles.innerWrapper}>
                {salesRep.name && (
                    <StyledWrapper {...styles.infoWrapper} as="span">
                        <Typography {...styles.nameText}>
                            {salesRep.name}
                            {salesRep.title && ` | ${salesRep.title}`}
                        </Typography>
                    </StyledWrapper>
                )}
                {salesRep.email && (
                    <StyledWrapper {...styles.infoWrapper} as="span">
                        <Typography {...styles.infoText}>
                            <Icon {...styles.emailIcon} title={translate("Email")} />
                            <Link href={`mailto:${salesRep.email}`}>{salesRep.email}</Link>
                        </Typography>
                    </StyledWrapper>
                )}
                {telOffice && (
                    <StyledWrapper {...styles.infoWrapper} as="span">
                        <Typography {...styles.infoText}>
                            <Icon {...styles.phoneIcon} title={translate("Office")} />
                            <span>{telOffice}</span>
                        </Typography>
                    </StyledWrapper>
                )}
                {salesRep.phone1 && (
                    <StyledWrapper {...styles.infoWrapper} as="span">
                        <Typography {...styles.infoText}>
                            <Icon {...styles.phoneIcon} title={translate("Phone")} />
                            <span>{salesRep.phone1}</span>
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: AccountSettingsSalesRepInfos,
    definition: {
        allowedContexts: ["AccountSettingsPage"],
        group: "Account Settings",
        displayName: "Account Manager",
    },
};

export default widgetModule;
